import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useAppContext } from '../../context/AppContext';
import { useBrand } from '../../context/BrandContext';
import { useCopy } from '../../context/CopyContext';
import Header from '../Header';
import Heading from '../Heading';
import Button from '../Button';
import MultiLineCopy from '../MultiLineCopy';
import PoweredBy from '../PoweredBy';
import ItemsModalView from './ItemsModalView';

const AddEmptyView: React.FC = () => {
  const navigate = useNavigate();
  const { state, dispatch } = useAppContext();
  const { brandConfig, isDarkMode } = useBrand();
  const { getCopy } = useCopy();
  const [showItemsModal, setShowItemsModal] = useState(false);

  const handleCancel = () => {
    dispatch({ type: 'SET_STEP', payload: 2 });
    navigate('/before-start');
  };

  const handleContinue = () => {
    // Navigate to checking empty view for detection
    dispatch({ type: 'SET_STEP', payload: 4 });
    navigate('/checking-empty');
  };

  const handleItemsClick = () => {
    setShowItemsModal(true);
  };

  const handleCloseItemsModal = () => {
    setShowItemsModal(false);
  };

  return (
    <div className="bg-brand-background min-h-screen flex flex-col relative overflow-hidden" data-node-id="73:821">
      {/* Animated Teal Glow Effect at Bottom */}
      <motion.div
        className="absolute bottom-[-675px] left-0  w-[1080px] h-[1080px] rounded-[1000px] pointer-events-none"
        style={{
          background: brandConfig.colors.primary[isDarkMode ? 'dark' : 'light'],
          filter: 'blur(200px)',
        }}
        animate={{
          y: [0, -100, 0],          
          opacity: [.25, .75, .25],
          scale: [1, 1.25, 1],
        }}
        transition={{
          duration: 4,
          repeat: Infinity,
          ease: "easeInOut"
        }}
      />
      
      {/* Subtle Blur Overlay for Bottom Area */}
      {/* <div 
        className="absolute bottom-0 left-0 right-0 h-[400px] pointer-events-none"
        style={{
          background: 'rgba(255, 255, 255, 0.10)',
          backdropFilter: 'blur(100px)',
        }}
      /> */}

      {/* Content Container */}
      <div className="relative z-10 flex-1 flex flex-col gap-24">
        
        {/* Header Section - with padding */}
        <div className="flex justify-center mx-20 mt-20">
          <div className="w-full max-w-[920px]">
            <Header 
              showItems={state.itemsCount > 0} 
              itemsNumber={state.itemsCount.toString()}
              onItemsClick={handleItemsClick}
            />
          </div>
        </div>

        {/* Main Content Area - with padding */}
        <motion.div 
          className="flex-1 grid grid-rows-3 gap-0 relative mx-20"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 20 }}
          transition={{ duration: 0.6, ease: [0.25, 0.1, 0.25, 1] }}
        >
          {/* Rows 1 & 2 - Main heading spanning two rows */}
          <div className="row-span-2 flex items-center justify-center">
            <div className="text-center w-full max-w-[920px]">
              <motion.div 
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                transition={{ 
                  duration: 0.6, 
                  ease: [0.25, 0.1, 0.25, 1]
                }}
              >
                <Heading 
                  title={getCopy('addEmpty.heading')}
                  subtitle={getCopy('addEmpty.subheading').replace('{ordinal}', state.itemsCount.toString() === "0" ? "first" : "next")}
                />
              </motion.div>
            </div>
          </div>

          {/* Row 3 - Did you know section */}
          <div className="flex items-end justify-center">
            <motion.div 
              className="text-center w-full max-w-[680px]"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3, ease: [0.25, 0.1, 0.25, 1] }}
            >
              <div className="font-brand-secondary font-light leading-none not-italic text-[32px] text-center text-brand-text-primary">
                <MultiLineCopy 
                  text={getCopy('addEmpty.helpText')}
                  className="leading-[42px]"
                />
              </div>
            </motion.div>
          </div>
        </motion.div>

        {/* Bottom Section - with padding */}
        <div className="flex flex-col gap-16 items-center mx-20 mb-10">
          {/* Buttons */}
          <div className="flex gap-4 justify-center">
            <Button 
              variant="secondary" 
              onClick={handleCancel}
            >
              {getCopy('addEmpty.buttons.secondary')}
            </Button>
            <Button 
              variant="primary" 
              onClick={handleContinue}
              className="min-w-[200px]"
            >
              Continue
            </Button>
          </div>

          {/* Footer */}
          <div className="flex justify-center">
            <PoweredBy />
          </div>
        </div>
      </div>

      {/* Items Modal */}
      <ItemsModalView
        isOpen={showItemsModal}
        onClose={handleCloseItemsModal}
      />
    </div>
  );
};

export default AddEmptyView;
